const offerFamilies = new Set(['download', 'course', 'report', 'enterprise', 'professional']);

const syncProductOfferContext = () => {
  const offerContext = document.querySelector('.epg-offer-context[data-epg-offer-family]');

  if (!offerContext) {
    delete document.body.dataset.epgOfferFamily;
    delete document.body.dataset.epgPurchaseMode;
    delete document.body.dataset.epgProductHandle;
    return;
  }

  const family = offerContext.dataset.epgOfferFamily;
  const purchaseMode = offerContext.dataset.epgPurchaseMode || 'direct';
  const productHandle = offerContext.dataset.epgProductHandle || '';

  if (!offerFamilies.has(family)) return;

  document.body.dataset.epgOfferFamily = family;
  document.body.dataset.epgPurchaseMode = purchaseMode;
  document.body.dataset.epgProductHandle = productHandle;

  document.querySelectorAll('.product-information, .epg-product-story').forEach((element) => {
    element.dataset.epgOfferFamily = family;
  });
};

syncProductOfferContext();
document.addEventListener('shopify:section:load', syncProductOfferContext);
window.addEventListener('pageshow', syncProductOfferContext);

const enhanceFindMySolutionPage = () => {
  if (!document.body.classList.contains('epg-page-handle-find-my-solution')) return;

  const formatter = document.querySelector('#MainContent .shopify-block.rte > rte-formatter');

  if (!formatter || formatter.dataset.epgEnhanced === 'true') return;

  formatter.dataset.epgEnhanced = 'true';
  formatter.classList.add('epg-solution-grid');

  let activeCard = null;
  let cardIndex = 0;

  [...formatter.children].forEach((element) => {
    if (element.tagName === 'H2') {
      cardIndex += 1;
      activeCard = document.createElement('section');
      activeCard.className = 'epg-solution-card';
      activeCard.dataset.epgIndex = String(cardIndex).padStart(2, '0');
      formatter.insertBefore(activeCard, element);
    }

    if (activeCard) activeCard.append(element);
  });
};

const enhanceFaqPage = () => {
  if (!document.body.classList.contains('epg-page-handle-frequently-asked-questions')) return;

  const formatter = document.querySelector('#MainContent .shopify-block.rte > rte-formatter');

  if (!formatter || formatter.dataset.epgEnhanced === 'true') return;

  formatter.dataset.epgEnhanced = 'true';
  formatter.classList.add('epg-faq-list');

  [...formatter.children].forEach((paragraph) => {
    if (paragraph.tagName !== 'P') return;

    const strong = paragraph.querySelector(':scope > strong:first-child');
    const label = strong?.textContent.replace(/\s+/g, ' ').trim();

    if (!label) return;

    if (!/^Q:/i.test(label)) {
      const groupTitle = document.createElement('h2');
      groupTitle.className = 'epg-faq-group-title';
      groupTitle.textContent = label;
      paragraph.replaceWith(groupTitle);
      return;
    }

    const details = document.createElement('details');
    details.className = 'epg-faq-item';

    const summary = document.createElement('summary');
    summary.textContent = label.replace(/^Q:\s*/i, '');

    const answer = document.createElement('div');
    answer.className = 'epg-faq-item__answer';

    strong.remove();

    if (paragraph.firstElementChild?.tagName === 'BR') {
      paragraph.firstElementChild.remove();
    }

    while (paragraph.firstChild) answer.append(paragraph.firstChild);

    details.append(summary, answer);
    paragraph.replaceWith(details);
  });
};

const getPageFormatter = () =>
  document.querySelector('#MainContent .shopify-block.rte > rte-formatter');

const createPageVisual = ({ datasetKey, modifier, alt, caption, width = 1600, height = 1000 }) => {
  const source = document.body.dataset[datasetKey];

  if (!source) return null;

  const figure = document.createElement('figure');
  figure.className = `epg-page-visual epg-page-visual--${modifier}`;

  const image = document.createElement('img');
  image.src = source;
  image.alt = alt;
  image.width = width;
  image.height = height;
  image.loading = 'lazy';
  image.decoding = 'async';

  figure.append(image);

  if (caption) {
    const figcaption = document.createElement('figcaption');
    figcaption.textContent = caption;
    figure.append(figcaption);
  }

  return figure;
};

const applyIllustrativeVideoPoster = (video, source, disclosure) => {
  if (source) video.poster = source;

  video.preload = 'metadata';
  video.playsInline = true;
  video.dataset.epgIllustrativePoster = 'true';

  const insertionTarget =
    window.getComputedStyle(video).position === 'absolute' && video.parentElement ? video.parentElement : video;

  if (insertionTarget.nextElementSibling?.classList.contains('epg-video-disclosure')) return;

  const note = document.createElement('p');
  note.className = 'epg-video-disclosure';
  note.textContent = disclosure;
  insertionTarget.after(note);
};

const createProofStrip = (items, modifier) => {
  const list = document.createElement('dl');
  list.className = `epg-proof-strip epg-proof-strip--${modifier}`;

  items.forEach(({ value, label }) => {
    const item = document.createElement('div');
    const term = document.createElement('dt');
    const description = document.createElement('dd');
    term.textContent = value;
    description.textContent = label;
    item.append(term, description);
    list.append(item);
  });

  return list;
};

const createSectionJumpNav = (sections, label, modifier) => {
  const nav = document.createElement('nav');
  nav.className = `epg-section-jump-nav epg-section-jump-nav--${modifier}`;
  nav.setAttribute('aria-label', label);

  sections.forEach((section, index) => {
    const heading = section.querySelector('h2, h3');

    if (!heading) return;

    if (!section.id) section.id = `epg-${modifier}-section-${index + 1}`;

    const link = document.createElement('a');
    link.href = `#${section.id}`;
    link.textContent = heading.textContent.replace(/\s+/g, ' ').trim();
    nav.append(link);
  });

  return nav.children.length ? nav : null;
};

const createPlatformPreview = (platform) => {
  const isCcap = platform === 'ccap';
  const preview = document.createElement('section');
  preview.className = `epg-platform-preview epg-platform-preview--${platform}`;
  preview.setAttribute(
    'aria-label',
    isCcap
      ? 'Illustrative CCAP compliance workflow concept - not a product screenshot'
      : 'Illustrative GADSuite reporting workflow concept - not a product screenshot'
  );

  const labels = isCcap
    ? ['Evidence ownership', 'Review cadence', 'Findings and actions', 'Audit readiness']
    : ['Unit records', 'Event logging', 'Performance review', 'Reporting preparation'];

  const header = document.createElement('div');
  header.className = 'epg-platform-preview__header';
  header.innerHTML = `
    <span class="epg-platform-preview__window-controls" aria-hidden="true"><i></i><i></i><i></i></span>
    <strong>${isCcap ? 'CCAP workflow' : 'GADSuite Pro workflow'}</strong>
    <span class="epg-platform-preview__concept">Illustrative workflow concept - not a product screenshot</span>
  `;

  const body = document.createElement('div');
  body.className = 'epg-platform-preview__body';

  const primary = document.createElement('div');
  primary.className = 'epg-platform-preview__primary';
  const primaryHeading = document.createElement('span');
  primaryHeading.className = 'epg-platform-preview__label';
  primaryHeading.textContent = isCcap ? 'Illustrative compliance workflow' : 'Illustrative generation-data workflow';
  primary.append(primaryHeading);

  labels.forEach((text) => {
    const row = document.createElement('div');
    row.className = 'epg-platform-preview__row';
    row.innerHTML = `<span>${text}</span><i aria-hidden="true"></i>`;
    primary.append(row);
  });

  const secondary = document.createElement('div');
  secondary.className = 'epg-platform-preview__secondary';
  secondary.innerHTML = `
    <span class="epg-platform-preview__label">Illustrative workflow pattern</span>
    <div class="epg-platform-preview__ring" aria-hidden="true"></div>
    <div class="epg-platform-preview__status"><i></i><span>Structured review</span></div>
    <div class="epg-platform-preview__status"><i></i><span>Clear ownership</span></div>
    <div class="epg-platform-preview__status"><i></i><span>Export-ready record</span></div>
  `;

  body.append(primary, secondary);
  preview.append(header, body);
  return preview;
};

const enhanceEarlyAccessPage = () => {
  const isEarlyAccess =
    document.body.classList.contains('epg-page-handle-ccap-early-access') ||
    document.body.classList.contains('epg-page-handle-gadsuite-early-access');

  if (!isEarlyAccess) return;

  const formatter = getPageFormatter();

  if (!formatter || formatter.dataset.epgEarlyAccessEnhanced === 'true') return;

  formatter.dataset.epgEarlyAccessEnhanced = 'true';
  formatter.classList.add('epg-early-access-layout');

  const children = [...formatter.children];
  children[0]?.classList.add('epg-early-access-status');
  children[1]?.classList.add('epg-early-access-intro');

  const platform = document.body.classList.contains('epg-page-handle-ccap-early-access') ? 'ccap' : 'gadsuite';
  const preview = createPlatformPreview(platform);
  const insertionPoint = children[2] || null;
  formatter.insertBefore(preview, insertionPoint);

  const grid = document.createElement('div');
  grid.className = 'epg-early-access-grid';

  formatter.insertBefore(grid, insertionPoint);

  let activeCard = null;
  let cardIndex = 0;

  children.slice(2).forEach((element) => {
    if (element.tagName === 'H2') {
      cardIndex += 1;
      activeCard = document.createElement('section');
      activeCard.className = 'epg-early-access-card';
      activeCard.dataset.epgIndex = String(cardIndex).padStart(2, '0');
      grid.append(activeCard);
    }

    if (activeCard) activeCard.append(element);
  });

  grid.querySelectorAll('p').forEach((paragraph) => {
    const links = [...paragraph.querySelectorAll(':scope > a')];
    const text = paragraph.textContent.replace(/\s+/g, ' ').trim();

    if (links.length === 1 && text === links[0].textContent.replace(/\s+/g, ' ').trim()) {
      paragraph.classList.add('epg-early-access-cta');
      links[0].classList.add('epg-early-access-cta__link');
    }
  });
};

const enhanceSoftwarePage = () => {
  if (!document.body.classList.contains('epg-page-handle-software')) return;

  document.body.classList.add('epg-page-catalog-landing');

  const formatter = getPageFormatter();

  if (!formatter || formatter.dataset.epgSoftwareEnhanced === 'true') return;

  formatter.dataset.epgSoftwareEnhanced = 'true';
  formatter.classList.add('epg-software-layout');

  const children = [...formatter.children];
  children[0]?.classList.add('epg-software-intro');

  const visual = createPageVisual({
    datasetKey: 'epgVisualSoftware',
    modifier: 'software',
    alt: 'Conceptual electric utility software workspace with grid, reliability, compliance, and reporting dashboards',
    caption: 'Illustrative utility software environment - not a product screenshot.',
    width: 1586,
    height: 992
  });

  if (visual) formatter.insertBefore(visual, children[1] || null);

  document.querySelectorAll('#MainContent video').forEach((video) => {
    applyIllustrativeVideoPoster(
      video,
      document.body.dataset.epgVisualSoftware,
      'Illustrative poster image - not a product screenshot.'
    );
  });

  const grid = document.createElement('div');
  grid.className = 'epg-software-grid';

  if (children[1]) formatter.insertBefore(grid, children[1]);

  let activeCard = null;
  let cardIndex = 0;

  children.slice(1).forEach((element) => {
    if (element.tagName === 'H2') {
      cardIndex += 1;
      activeCard = document.createElement('section');
      activeCard.className = 'epg-software-card';
      activeCard.dataset.epgIndex = String(cardIndex).padStart(2, '0');
      grid.append(activeCard);
    }

    if (activeCard) activeCard.append(element);
  });

};

const enhanceBenchmarkReportsPage = () => {
  if (!document.body.classList.contains('epg-page-handle-benchmark-reports')) return;

  document.body.classList.add('epg-page-catalog-landing');

  const formatter = getPageFormatter();

  if (!formatter || formatter.dataset.epgBenchmarkEnhanced === 'true') return;

  formatter.dataset.epgBenchmarkEnhanced = 'true';
  formatter.classList.add('epg-benchmark-layout');

  const children = [...formatter.children];
  const grid = document.createElement('div');
  grid.className = 'epg-benchmark-grid';

  if (children[0]) formatter.insertBefore(grid, children[0]);

  let activeCard = null;
  let cardIndex = 0;

  children.forEach((element) => {
    const strong = element.matches('p') ? element.querySelector(':scope > strong:first-child') : null;
    const isHeading = strong && strong.textContent.replace(/\s+/g, ' ').trim() === element.textContent.replace(/\s+/g, ' ').trim();

    if (isHeading) {
      cardIndex += 1;
      activeCard = document.createElement('section');
      activeCard.className = 'epg-benchmark-card';
      activeCard.dataset.epgIndex = String(cardIndex).padStart(2, '0');
      grid.append(activeCard);
      element.classList.add('epg-benchmark-card__title');
    }

    if (activeCard) activeCard.append(element);
  });

  const visual = createPageVisual({
    datasetKey: 'epgVisualBenchmark',
    modifier: 'benchmark',
    alt: 'Open utility benchmark intelligence report with charts and an executive analytics dashboard',
    caption: 'Illustrative benchmark-report spread - final deliverables vary by scope.',
    width: 1568,
    height: 1003
  });
  const proof = createProofStrip(
    [
      { value: '3 to 7 business days', label: 'standard custom-report delivery' },
      { value: 'Peer context', label: 'comparisons shaped for utility decisions' },
      { value: 'Custom scope', label: 'built around the questions your team needs answered' }
    ],
    'benchmark'
  );

  const firstCard = grid.firstElementChild;
  if (firstCard && visual) firstCard.after(visual);
  if (visual) visual.after(proof);
  else if (firstCard) firstCard.after(proof);

  document.querySelectorAll('#MainContent video').forEach((video) => {
    applyIllustrativeVideoPoster(
      video,
      document.body.dataset.epgVisualBenchmark,
      'Illustrative poster image - not a sample report or final deliverable.'
    );
  });
};

const enhanceAboutPage = () => {
  if (!document.body.classList.contains('epg-page-handle-about-epg-solutions')) return;

  const formatter = getPageFormatter();

  if (!formatter || formatter.dataset.epgAboutEnhanced === 'true') return;

  formatter.dataset.epgAboutEnhanced = 'true';
  formatter.classList.add('epg-about-layout');

  [...formatter.querySelectorAll(':scope > p')].forEach((paragraph) => {
    if (!paragraph.textContent.trim()) paragraph.remove();
  });

  const lead = formatter.querySelector(':scope > h2');

  if (lead) {
    const leadParagraph = document.createElement('p');
    leadParagraph.className = 'epg-about-lead';
    leadParagraph.innerHTML = lead.innerHTML;
    lead.replaceWith(leadParagraph);
  }

  const paragraphs = [...formatter.querySelectorAll(':scope > p:not(.epg-about-lead)')];
  const grid = document.createElement('div');
  grid.className = 'epg-about-grid';

  if (paragraphs[0]) formatter.insertBefore(grid, paragraphs[0]);

  paragraphs.forEach((paragraph, index) => {
    const card = document.createElement('section');
    card.className = 'epg-about-card';
    card.dataset.epgIndex = String(index + 1).padStart(2, '0');
    card.append(paragraph);
    grid.append(card);
  });

  const proof = createProofStrip(
    [
      { value: '100+', label: 'years of combined utility experience' },
      { value: 'Lean team', label: 'close to every customer and deliverable' },
      { value: 'Utility-only', label: 'focus across every EPG product' }
    ],
    'about'
  );
  grid.before(proof);

  const callout = document.createElement('section');
  callout.className = 'epg-about-cta';
  callout.innerHTML = `
    <div><span>Start with the right resource</span><h2>Built for the work utilities actually do.</h2></div>
    <p>Tell us the operating, compliance, training, or intelligence outcome your team needs.</p>
    <div class="epg-about-cta__actions">
      <a href="/pages/find-my-solution">Find my solution</a>
      <a href="/pages/contact">Talk with EPG Solutions</a>
    </div>
  `;
  grid.after(callout);
};

const enhanceNercPrepPage = () => {
  if (!document.body.classList.contains('epg-page-handle-nerc-rc-exam-prep')) return;

  const formatter = getPageFormatter();

  if (!formatter || formatter.dataset.epgNercPrepEnhanced === 'true') return;

  formatter.dataset.epgNercPrepEnhanced = 'true';
  formatter.classList.add('epg-nerc-prep-layout');

  const sections = [...formatter.querySelectorAll(':scope > section')];

  sections.forEach((section, index) => {
    section.classList.add('epg-nerc-prep-section');
    section.dataset.epgIndex = String(index + 1).padStart(2, '0');
  });

  const hero = sections[0];
  const jumpNav = createSectionJumpNav(sections.slice(1), 'NERC exam preparation sections', 'nerc');
  const visual = createPageVisual({
    datasetKey: 'epgVisualNerc',
    modifier: 'nerc',
    alt: 'Electric grid operator training workstation with study materials and power-system displays',
    caption: 'Illustrative operator training environment.',
    width: 1568,
    height: 1003
  });

  if (hero && jumpNav) hero.after(jumpNav);
  if (jumpNav && visual) jumpNav.after(visual);
  else if (hero && visual) hero.after(visual);

  formatter.querySelectorAll('video').forEach((video) => {
    video.closest('section')?.classList.add('epg-nerc-video-section');
    applyIllustrativeVideoPoster(
      video,
      document.body.dataset.epgVisualNerc,
      'Illustrative poster image - not a GridCert course screenshot.'
    );
  });
};

const splitCssSelectors = (selectorText) => {
  const selectors = [];
  let active = '';
  let depth = 0;

  [...selectorText].forEach((character) => {
    if (character === '(' || character === '[') depth += 1;
    if (character === ')' || character === ']') depth = Math.max(0, depth - 1);

    if (character === ',' && depth === 0) {
      selectors.push(active.trim());
      active = '';
      return;
    }

    active += character;
  });

  if (active.trim()) selectors.push(active.trim());
  return selectors;
};

const scopeEmbeddedStyle = (formatter, scope, marker) => {
  const embeddedStyle = formatter.querySelector(':scope > style');

  if (!embeddedStyle?.sheet) return;

  const scopeSelector = (selector) => {
    const normalized = selector.trim();

    if (
      normalized === ':root' ||
      normalized === 'html' ||
      normalized === 'body' ||
      /^\[data-theme=["']light["']\]$/.test(normalized)
    ) {
      return scope;
    }

    if (/^\[data-theme=["']dark["']\]$/.test(normalized)) {
      return `${scope}[data-theme="dark"]`;
    }

    if (normalized.startsWith('html ')) return `${scope} ${normalized.slice(5)}`;
    if (normalized.startsWith('body ')) return `${scope} ${normalized.slice(5)}`;
    return `${scope} ${normalized}`;
  };

  const renderRule = (rule) => {
    if (rule.type === CSSRule.STYLE_RULE) {
      const selectors = splitCssSelectors(rule.selectorText).map(scopeSelector).join(', ');
      return `${selectors}{${rule.style.cssText}}`;
    }

    if (rule.cssRules) {
      const header = rule.cssText.slice(0, rule.cssText.indexOf('{')).trim();
      return `${header}{${[...rule.cssRules].map(renderRule).join('')}}`;
    }

    return rule.cssText;
  };

  const scopedStyle = document.createElement('style');
  scopedStyle.dataset[marker] = 'true';
  scopedStyle.textContent = [...embeddedStyle.sheet.cssRules].map(renderRule).join('\n');
  embeddedStyle.replaceWith(scopedStyle);
};

const enhanceSecurityTrustPage = () => {
  if (!document.body.classList.contains('epg-page-handle-security-trust')) return;

  const formatter = getPageFormatter();

  if (!formatter || formatter.dataset.epgSecurityEnhanced === 'true') return;

  formatter.dataset.epgSecurityEnhanced = 'true';
  formatter.classList.add('epg-security-layout');
  formatter.querySelector(':scope > main')?.classList.add('epg-security-main');

  scopeEmbeddedStyle(formatter, '.epg-security-layout', 'epgSecurityScoped');

  formatter
    .querySelectorAll(':scope > meta, :scope > title, :scope > link, :scope > nav, :scope > footer, :scope > script')
    .forEach((element) => element.remove());

  const securityMain = formatter.querySelector(':scope > main');
  const securitySections = securityMain ? [...securityMain.querySelectorAll(':scope > section')] : [];
  const securityNav = createSectionJumpNav(securitySections.slice(1), 'Security and trust sections', 'security');

  if (securityNav && securitySections[0]) securitySections[0].after(securityNav);

  document.documentElement.removeAttribute('data-theme');
};

const enhanceBenchmarkIntakePage = () => {
  if (!document.body.classList.contains('epg-page-handle-benchmark-report-intake')) return;

  const formatter = getPageFormatter();

  if (!formatter || formatter.dataset.epgBenchmarkIntakeEnhanced === 'true') return;

  formatter.dataset.epgBenchmarkIntakeEnhanced = 'true';
  formatter.classList.add('epg-benchmark-intake-layout');
  scopeEmbeddedStyle(formatter, '.epg-benchmark-intake-layout', 'epgBenchmarkIntakeScoped');
};

const enhanceSecureBenchmarkAccessPage = () => {
  if (!document.body.classList.contains('epg-page-handle-secure-benchmark-report-builder-access')) return;

  const formatter = getPageFormatter();

  if (!formatter || formatter.dataset.epgSecureAccessEnhanced === 'true') return;

  formatter.dataset.epgSecureAccessEnhanced = 'true';
  formatter.classList.add('epg-secure-access-layout');

  formatter.querySelector(':scope > h1')?.remove();

  const paragraphs = [...formatter.querySelectorAll(':scope > p')];
  paragraphs[0]?.classList.add('epg-secure-access-intro');
  paragraphs.find((paragraph) => paragraph.querySelector(':scope > strong'))?.classList.add('epg-secure-access-timing');

  paragraphs.forEach((paragraph) => {
    const links = [...paragraph.querySelectorAll(':scope > a')];

    if (links.length === 1 && paragraph.textContent.trim() === links[0].textContent.trim()) {
      paragraph.classList.add('epg-secure-access-cta');
      links[0].classList.add('epg-secure-access-cta__link');
    }
  });
};

const enhanceFreeChecklistPage = () => {
  if (!document.body.classList.contains('epg-page-handle-free-nerc-cip-compliance-checklist')) return;

  const formatter = getPageFormatter();

  if (!formatter || formatter.dataset.epgChecklistEnhanced === 'true') return;

  formatter.dataset.epgChecklistEnhanced = 'true';
  formatter.classList.add('epg-checklist-layout');

  [...formatter.querySelectorAll(':scope > p')].forEach((paragraph) => {
    if (!paragraph.textContent.trim()) paragraph.remove();
  });

  const paragraphs = [...formatter.querySelectorAll(':scope > p')];
  paragraphs[0]?.classList.add('epg-checklist-lead');

  const checklistItems = paragraphs.filter((paragraph) => paragraph.textContent.trim().startsWith('\u2705'));

  if (checklistItems.length) {
    const grid = document.createElement('div');
    grid.className = 'epg-checklist-grid';
    formatter.insertBefore(grid, checklistItems[0]);

    checklistItems.forEach((paragraph) => {
      paragraph.classList.add('epg-checklist-item');
      paragraph.textContent = paragraph.textContent.trim().replace(/^\u2705\s*/u, '');
      grid.append(paragraph);
    });
  }

  const headingLabels = new Set(["What's Inside the Checklist:", 'Who This Is For:', 'Get the Free Checklist:']);

  formatter.querySelectorAll(':scope > p').forEach((paragraph) => {
    const text = paragraph.textContent.replace(/\s+/g, ' ').trim();

    if (headingLabels.has(text)) paragraph.classList.add('epg-checklist-heading');
    if (/^Send an email to /i.test(text) || /^Or call us directly:/i.test(text)) {
      paragraph.classList.add('epg-checklist-cta');
    }
    if (/^Already know you have gaps\?/i.test(text)) paragraph.classList.add('epg-checklist-upsell');
  });
};

const enhanceDuplicatePageTitle = () => {
  if (!document.body.classList.contains('epg-page-page')) return;

  const formatter = getPageFormatter();
  const outerTitleBlock = document.querySelector(
    '#MainContent > .shopify-section:only-child .section-content-wrapper > .text-block:first-child'
  );
  const outerTitle = outerTitleBlock?.querySelector('h1')?.textContent.replace(/\s+/g, ' ').trim();

  if (!formatter || !outerTitleBlock || !outerTitle) return;

  const hasMatchingInnerTitle = [...formatter.querySelectorAll('h1')].some(
    (heading) => heading.textContent.replace(/\s+/g, ' ').trim() === outerTitle
  );

  if (hasMatchingInnerTitle) outerTitleBlock.classList.add('epg-duplicate-page-title');
};

const enhanceArticlePage = () => {
  if (!document.body.classList.contains('epg-page-article')) return;

  const header = document.querySelector('#MainContent > .shopify-section:first-child header');
  const content = document.querySelector('#MainContent .blog-post-content');

  if (!header || header.dataset.epgArticleEnhanced === 'true') return;

  header.dataset.epgArticleEnhanced = 'true';
  header.classList.add('epg-article-hero');
  content?.classList.add('epg-article-body');

  const pathnameParts = window.location.pathname.split('/').filter(Boolean);
  const blogPath = pathnameParts.length >= 2 ? `/${pathnameParts.slice(0, 2).join('/')}` : '/blogs/regulatory-pulse';
  const breadcrumb = document.createElement('nav');
  breadcrumb.className = 'epg-article-breadcrumb';
  breadcrumb.setAttribute('aria-label', 'Breadcrumb');

  const blogLink = document.createElement('a');
  blogLink.href = blogPath;
  blogLink.textContent = 'Regulatory Pulse';

  const current = document.createElement('span');
  current.textContent = 'Briefing';
  breadcrumb.append(blogLink, current);
  header.prepend(breadcrumb);

  const outerTitle = header.querySelector('h1')?.textContent.replace(/\s+/g, ' ').trim();

  if (outerTitle && content) {
    [...content.querySelectorAll('h1, h2')].forEach((heading) => {
      if (heading.textContent.replace(/\s+/g, ' ').trim() === outerTitle) heading.hidden = true;
    });
  }
};

const enhanceRichTextPages = () => {
  enhanceFindMySolutionPage();
  enhanceFaqPage();
  enhanceEarlyAccessPage();
  enhanceSoftwarePage();
  enhanceBenchmarkReportsPage();
  enhanceAboutPage();
  enhanceNercPrepPage();
  enhanceSecurityTrustPage();
  enhanceBenchmarkIntakePage();
  enhanceSecureBenchmarkAccessPage();
  enhanceFreeChecklistPage();
  enhanceDuplicatePageTitle();
  enhanceArticlePage();
};

enhanceRichTextPages();
document.addEventListener('shopify:section:load', enhanceRichTextPages);
window.addEventListener('pageshow', enhanceRichTextPages);

const mainContent = document.querySelector('#MainContent');

if (mainContent) {
  new MutationObserver(() => {
    syncProductOfferContext();
    enhanceRichTextPages();
  }).observe(mainContent, { childList: true, subtree: true });
}

const contactPage = document.body.classList.contains('epg-page-handle-contact');

if (contactPage) {
  const contactDetails = document.querySelector(
    '#MainContent > .shopify-section:first-child rte-formatter.rte'
  );

  let contactServiceMode = false;

  contactDetails?.querySelectorAll(':scope > p').forEach((paragraph) => {
    const text = paragraph.textContent.replace(/\u00a0/g, ' ').trim();

    if (!text) {
      paragraph.hidden = true;
      return;
    }

    if (/^[-\u2013\u2014\s]+$/.test(text)) {
      paragraph.hidden = true;
      return;
    }

    if (text === 'Get in Touch with EPG Solutions') {
      paragraph.classList.add('epg-contact-intro-title');
    }

    const contactLabelPrefixes = ['\u{1F4E7}', '\u{1F4DE}', '\u{1F4CD}', '\u{1F3E2}', '\u{1F552}'];

    if (contactLabelPrefixes.some((prefix) => text.startsWith(prefix))) {
      paragraph.classList.add('epg-contact-label');
    }

    if (text === 'What We Help With' || text === 'Serving Electric Utilities Nationwide') {
      paragraph.classList.add('epg-contact-heading');
    }

    if (text === 'What We Help With') {
      contactServiceMode = true;
    } else if (text === 'Serving Electric Utilities Nationwide') {
      contactServiceMode = false;
    } else if (contactServiceMode) {
      paragraph.classList.add('epg-contact-service');
    }

    if (text === 'support@epgsolutions.services') {
      const link = document.createElement('a');
      link.href = 'mailto:support@epgsolutions.services';
      link.textContent = text;
      paragraph.replaceChildren(link);
    }

    if (text === '816-266-4838') {
      const link = document.createElement('a');
      link.href = 'tel:+18162664838';
      link.textContent = text;
      paragraph.replaceChildren(link);
    }
  });
}
